import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { authModule } from '../../api/modules/auth.module';
import { showToast } from '../../utils/toast';
import { EmailVerificationView } from './EmailVerification.view';

/**
 * Email verification, the container. Query-string reading, the verify call, the
 * resend call, failure classification and the toasts.
 *
 * Everything visual lives in `EmailVerification.view.jsx`, which is pure, so the
 * state explorer can mount every sub-state without the API running.
 *
 * This page no longer imports `../Login/login.css`. THAT FILE MUST STAY for now:
 * `Signup` and `LandingPage` still import it, which is exactly the coupling this
 * conversion exists to remove. It goes when the last of them is converted.
 *
 * CLASSIFYING THE FAILURE
 * -----------------------
 * `authModule.verifyEmail` returns `{ success, message }` and nothing else, no
 * code, so the only signal available is the message text. Same technique
 * `login.jsx` already uses for its analytics `failure_reason`. If the API ever
 * grows a code, this function is the single place that changes.
 */

const STATUS = {
  AWAITING: 'awaiting',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  ALREADY: 'already-verified',
  EXPIRED: 'expired',
  FAILED: 'failed',
};

function classify(message) {
  const text = String(message || '').toLowerCase();
  if (text.includes('expire')) return STATUS.EXPIRED;
  if (text.includes('already')) return STATUS.ALREADY;
  return STATUS.FAILED;
}

export default function EmailVerification() {
  const location = useLocation();
  const [status, setStatus] = useState(STATUS.AWAITING);
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  // A token is verified once. Without this guard a re-render of the location
  // object would fire the POST again and the second call would come back
  // "already used", turning a good verification into a failure screen.
  const verifiedToken = useRef(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get('token');
    const queryEmail = queryParams.get('email');

    if (queryEmail) {
      setEmail(queryEmail);
    }

    if (!token || verifiedToken.current === token) return;
    verifiedToken.current = token;

    let cancelled = false;
    setStatus(STATUS.VERIFYING);
    setError(null);

    authModule
      .verifyEmail(token)
      .then((result) => {
        if (cancelled) return;
        if (result.success) {
          // A success whose message says "already verified" is not a fresh
          // verification and must not claim to be one.
          const already = String(result.message || '').toLowerCase().includes('already');
          setStatus(already ? STATUS.ALREADY : STATUS.VERIFIED);
          showToast.success(result.message || 'Email verified successfully!');
        } else {
          setStatus(classify(result.message));
          setError(result.message || 'Verification failed. Please try again.');
          showToast.error(result.message || 'Verification failed. Please try again.');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setStatus(STATUS.FAILED);
        setError('An unexpected error occurred. Please try again.');
        showToast.error('An unexpected error occurred. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [location]);

  const handleResend = useCallback(async () => {
    if (!email) {
      showToast.error('Email address is required to resend verification.');
      return;
    }

    setResending(true);
    setError(null);
    setSent(false);

    try {
      const result = await authModule.resendVerification(email);

      if (result.success) {
        setSent(true);
        showToast.success(result.message || 'Verification email sent!');
      } else {
        setError(result.message || 'Failed to resend verification email.');
        showToast.error(result.message || 'Failed to resend verification email.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      showToast.error('An unexpected error occurred. Please try again.');
    } finally {
      setResending(false);
    }
  }, [email]);

  return (
    <EmailVerificationView
      status={status}
      email={email}
      onEmailChange={setEmail}
      onResend={handleResend}
      resending={resending}
      sent={sent}
      error={error}
    />
  );
}
